import React, { useState } from "react";
import * as feeCommissionService from "@/shared/api/services/feeCommissionService";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Zap, DollarSign, Percent, AlertCircle, Calculator } from "lucide-react";

const FEE_TYPES = ["FIXED", "PERCENTAGE"];

/**
 * Reusable Fee Commission Configuration Section
 * Used in entity forms (Engine, Reseller, Merchant)
 * 
 * Props:
 *  - feeData: { depositFee, withdrawalFee, depositCommission, withdrawalCommission, etc }
 *  - onChange: Handler for fee changes
 *  - entityType: ADMIN, ENGINE, RESELLER, MERCHANT
 *  - entityId: Entity ID (optional, for non-admin)
 *  - onConstraintChange: Handler for constraint changes
 *  - showConstraints: Show min/max constraints section (default: false)
 *  - description: Description for fee setup
 */
export default function FeeCommissionSection({
  feeData = {},
  onChange,
  entityType = "ADMIN",
  entityId = null,
  onConstraintChange,
  showConstraints = false,
  description,
}) {
  const { t } = useLanguage();
  const sectionDescription = description || t("configureDepositAndWithdrawalFees");
  const [calcResult, setCalcResult] = useState(null);
  const [showCalcDetails, setShowCalcDetails] = useState(false);

  // Calculate fee breakdown for preview
  const calculateMutation = useMutation({
    mutationFn: (input) => feeCommissionService.calculateFeeBreakdown(input),
    onSuccess: (result) => {
      setCalcResult(result);
      setShowCalcDetails(true);
    },
    onError: (err) => {
      toast.error("Calculation failed: " + (err?.message || "Unknown error"));
    },
  });

  const handleFeeChange = (field, value) => {
    onChange({
      ...feeData,
      [field]: value,
    });
  };

  const handleCalculatePreview = () => {
    calculateMutation.mutate({
      transactionType: "DEPOSIT",
      amount: 1000,
      entityType,
      entityId: entityId || null,
    });
  };

  return (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="w-full overflow-x-auto justify-start whitespace-nowrap">
        <TabsTrigger value="basic" className="gap-2 shrink-0">
          <Zap className="h-4 w-4" />
          {t("depositAndWithdrawal")}
        </TabsTrigger>
        <TabsTrigger value="commission" className="gap-2 shrink-0">
          <Percent className="h-4 w-4" />
          {t("commission")}
        </TabsTrigger>
        {showConstraints && (
          <TabsTrigger value="constraints" className="gap-2 shrink-0">
            <AlertCircle className="h-4 w-4" />
            {t("constraints")}
          </TabsTrigger>
        )}
      </TabsList>

      {/* Basic Tab - Deposit/Withdrawal Fees */}
      <TabsContent value="basic">
        <Card>
          <CardHeader>
            <CardTitle>{t("depositAndWithdrawalFees")}</CardTitle>
            <CardDescription>{sectionDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Deposit Fee */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold">{t("depositFee")}</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("amount")} (RS)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={feeData.depositFeeAmount || 0}
                    onChange={(e) => handleFeeChange("depositFeeAmount", parseFloat(e.target.value))}
                    placeholder={t("amountPlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("type")}</Label>
                  <Select
                    value={feeData.depositFeeType || "FIXED"}
                    onValueChange={(v) => handleFeeChange("depositFeeType", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIXED">{t("fixedAmount")} (RS)</SelectItem>
                      <SelectItem value="PERCENTAGE">{t("percentage")} (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Withdrawal Fee */}
            <div className="space-y-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                <h3 className="font-semibold">{t("withdrawalFee")}</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("amount")} (RS)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={feeData.withdrawalFeeAmount || 0}
                    onChange={(e) => handleFeeChange("withdrawalFeeAmount", parseFloat(e.target.value))}
                    placeholder={t("amountPlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("type")}</Label>
                  <Select
                    value={feeData.withdrawalFeeType || "FIXED"}
                    onValueChange={(v) => handleFeeChange("withdrawalFeeType", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIXED">{t("fixedAmount")} (RS)</SelectItem>
                      <SelectItem value="PERCENTAGE">{t("percentage")} (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Commission Tab */}
      <TabsContent value="commission">
        <Card>
          <CardHeader>
            <CardTitle>{t("commissionRates")}</CardTitle>
            <CardDescription>{t("commissionEarnedFromTransactions")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Deposit Commission */}
            <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-purple-600" />
                <h3 className="font-semibold">{t("depositCommission")}</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("amount")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={feeData.depositCommissionAmount || 0}
                    onChange={(e) => handleFeeChange("depositCommissionAmount", parseFloat(e.target.value))}
                    placeholder={t("amountPlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("type")}</Label>
                  <Select
                    value={feeData.depositCommissionType || "PERCENTAGE"}
                    onValueChange={(v) => handleFeeChange("depositCommissionType", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIXED">{t("fixedAmount")} (RS)</SelectItem>
                      <SelectItem value="PERCENTAGE">{t("percentage")} (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Withdrawal Commission */}
            <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold">{t("withdrawalCommission")}</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("amount")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={feeData.withdrawalCommissionAmount || 0}
                    onChange={(e) => handleFeeChange("withdrawalCommissionAmount", parseFloat(e.target.value))}
                    placeholder={t("amountPlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("type")}</Label>
                  <Select
                    value={feeData.withdrawalCommissionType || "PERCENTAGE"}
                    onValueChange={(v) => handleFeeChange("withdrawalCommissionType", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIXED">{t("fixedAmount")} (RS)</SelectItem>
                      <SelectItem value="PERCENTAGE">{t("percentage")} (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Calculate Preview */}
            <div className="pt-4">
              <Button
                onClick={handleCalculatePreview}
                disabled={calculateMutation.isPending}
                className="w-full gap-2"
              >
                <Calculator className="h-4 w-4" />
                {calculateMutation.isPending ? t("calculating") : `${t("calculateFeePreview")} (1000RS)`}
              </Button>
            </div>

            {/* Calculation Result */}
            {calcResult && showCalcDetails && (
              <Alert className="bg-cyan-50 border-cyan-200">
                <AlertCircle className="h-4 w-4 text-cyan-600" />
                <AlertDescription className="text-cyan-800">
                  <p className="font-semibold mb-2">{t("feeBreakdownFor1000")}</p>
                  <div className="space-y-1 text-sm">
                    <p>{t("totalFees")}: <span className="font-mono font-bold">{calcResult.totalFeeAmount}RS</span></p>
                    <p>{t("adminFee")}: <span className="font-mono">{calcResult.adminFeeAmount}RS</span></p>
                    <p>{t("netAmount")}: <span className="font-mono font-bold">{calcResult.netAmount}RS</span></p>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Constraints Tab */}
      {showConstraints && (
        <TabsContent value="constraints">
          <Card>
            <CardHeader>
              <CardTitle>{t("feeConstraints")}</CardTitle>
              <CardDescription>{t("setMinMaxLimitsForChildEntities")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Deposit Constraints */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> {t("depositConstraints")}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("minFee")} (RS)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.minDepositFee || ""}
                      onChange={(e) => handleFeeChange("minDepositFee", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("maxFee")} (RS)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.maxDepositFee || ""}
                      onChange={(e) => handleFeeChange("maxDepositFee", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                </div>
              </div>

              {/* Withdrawal Constraints */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> {t("withdrawalConstraints")}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("minFee")} (RS)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.minWithdrawalFee || ""}
                      onChange={(e) => handleFeeChange("minWithdrawalFee", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("maxFee")} (RS)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.maxWithdrawalFee || ""}
                      onChange={(e) => handleFeeChange("maxWithdrawalFee", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                </div>
              </div>

              {/* Commission Constraints */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Percent className="h-4 w-4" /> {t("commissionConstraints")}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("minDepositCommission")} (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.minDepositCommission || ""}
                      onChange={(e) => handleFeeChange("minDepositCommission", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("maxDepositCommission")} (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.maxDepositCommission || ""}
                      onChange={(e) => handleFeeChange("maxDepositCommission", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("minWithdrawalCommission")} (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.minWithdrawalCommission || ""}
                      onChange={(e) => handleFeeChange("minWithdrawalCommission", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("maxWithdrawalCommission")} (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeData.maxWithdrawalCommission || ""}
                      onChange={(e) => handleFeeChange("maxWithdrawalCommission", e.target.value)}
                      placeholder={t("none")}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      )}
    </Tabs>
  );
}
